// src/contexts/AuthContext.js

import React, { createContext, useContext, useEffect, useState } from 'react'
import { auth, db } from '../firebase'
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth'
import {
  doc,
  onSnapshot,
} from 'firebase/firestore'

const AuthContext = createContext()

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [loading, setLoading] = useState(true)
  const [authed, setAuthed] = useState(false)
  const [userStatus, setUserStatus] = useState(null)

  // 1) Email/Password ile Giriş (Login)
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password)
  }

  // 2) Şifre sıfırlama
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email)
  }

  // 3) Çıkış yapma
  function logout() {
    return signOut(auth)
  }

  // 4) Mevcut kullanıcıyı izleme (onAuthStateChanged)
  useEffect(() => {
    let unsubscribeUserDoc = null

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (unsubscribeUserDoc) {
        unsubscribeUserDoc()
        unsubscribeUserDoc = null
      }

      if (user) {
        if (user.isAnonymous) {
          setCurrentUser({
            uid: user.uid,
            email: '',
            avatarUrl: '',
            nickname: '',
            isAnonymous: true,
            isEmergencyTeamMember: false,
          })
          setUserRole('guest')
          setUserStatus('active')
          setAuthed(true)
          setLoading(false)
          return
        }

        // Ardından Firestore'daki kullanıcının verilerini dinle
        const userDocRef = doc(db, 'users', user.uid)
        unsubscribeUserDoc = onSnapshot(userDocRef, (snapshot) => {
          if (snapshot.exists()) {

            const userData = snapshot.data()
            setCurrentUser({
              uid: user.uid,
              email: user.email,
              avatarUrl: userData.avatarUrl || '',
              nickname: userData.nickname || '',
              isEmergencyTeamMember: userData.isEmergencyTeamMember === true && userData.archived !== true,
              isAnonymous: false,
            })
            setUserRole(userData.role || 'user')
            setUserStatus(userData.status || 'active')
          } else {
            // Sadece admin tarafından users kaydı açılan hesaplar uygulamaya girebilir.
            setCurrentUser({
              uid: user.uid,
              email: user.email,
              avatarUrl: user.avatarUrl || '',
              nickname: user.nickname || '',
              isEmergencyTeamMember: false,
              isAnonymous: false,
            })
            setUserRole('user')
            setUserStatus('passive')
          }
          setAuthed(true)
          setLoading(false)
        })

      } else {
        // Kullanıcı yoksa state sıfırla
        setCurrentUser(null)
        setUserRole(null)
        setUserStatus(null)
        setAuthed(false)
        setLoading(false)
      }
    })

    // Cleanup
    return () => {
      unsubscribeAuth()
      if (unsubscribeUserDoc) unsubscribeUserDoc()
    }
  }, [])

  // Context olarak döndüreceğimiz değerler
  const value = {
    currentUser,
    userRole,
    authed,
    userStatus,
    loading,
    setAuthed,
    login,
    resetPassword,
    logout,
  }

  return (
    <AuthContext.Provider value={value}>
      {/* loading false olana kadar children'ı göstermeyelim */}
      {!loading && children}
    </AuthContext.Provider>
  )
}
